// MPlusInformation.js

import React from "react";
import {
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import useStyles from "./style";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

const VEHICLE_TYPE_OPTIONS = [
  { value: "MOTORCYCLE", label: "Moto (Zem)" },
  { value: "CAR", label: "Taxi standard / Voiture" },
];

const REPAYMENT_PREFERENCE_OPTIONS = ["12", "18", "24", "36"].map((months) => ({
  value: months,
  label: `${months} mois`,
}));

const GUARANTOR_RELATIONSHIP_OPTIONS = [
  { value: "FATHER", label: "Père" },
  { value: "MOTHER", label: "Mère" },
  { value: "BROTHER", label: "Frère" },
  { value: "SISTER", label: "Sœur" },
  { value: "SPOUSE", label: "Époux / Épouse" },
  { value: "FRIEND", label: "Ami(e)" },
  { value: "OTHER", label: "Autre" },
];

const DateField = ({ label, name, formikMPlus }) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
      label={label}
      value={formikMPlus.values[name] ? dayjs(formikMPlus.values[name]) : null}
      onChange={(newValue) => {
        const formattedDate = newValue ? dayjs(newValue).format("YYYY-MM-DD") : "";
        formikMPlus.setFieldValue(name, formattedDate);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          id={name}
          error={formikMPlus.touched[name] && Boolean(formikMPlus.errors[name])}
          helperText={
            formikMPlus.touched[name] && formikMPlus.errors[name] ? formikMPlus.errors[name] : ""
          }
          fullWidth
          size="small"
        />
      )}
    />
  </LocalizationProvider>
);

const TextInput = ({ label, name, formikMPlus, type = "text", optional = false, adornment, helperText }) => (
  <TextField
    name={name}
    id={name}
    label={optional ? `${label} (optionnel)` : label}
    type={type}
    value={formikMPlus.values[name]}
    onChange={formikMPlus.handleChange}
    error={Boolean(formikMPlus.errors[name] && formikMPlus.touched[name])}
    helperText={
      (formikMPlus.errors[name] && formikMPlus.touched[name] && formikMPlus.errors[name]) || helperText
    }
    InputProps={
      adornment ? { endAdornment: <InputAdornment position="end">{adornment}</InputAdornment> } : undefined
    }
    fullWidth
    variant="outlined"
    size="small"
  />
);

const SelectInput = ({ label, name, formikMPlus, options }) => (
  <TextField
    select
    name={name}
    id={name}
    label={label}
    value={formikMPlus.values[name]}
    onChange={formikMPlus.handleChange}
    error={Boolean(formikMPlus.errors[name] && formikMPlus.touched[name])}
    helperText={formikMPlus.errors[name] && formikMPlus.touched[name] && formikMPlus.errors[name]}
    fullWidth
    variant="outlined"
    size="small"
  >
    <MenuItem value="">-- Sélectionnez --</MenuItem>
    {options.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    ))}
  </TextField>
);

const MPlusInformation = ({ formikMPlus, setsubmitButton, setSkipButton }) => {
  const classes = useStyles();
  setsubmitButton(true);
  setSkipButton(true);

  return (
    <div className={classes.BasicInformationStyles}>
      <Grid container>
        <section className={classes.container}>
          <Grid item xs={12} className={classes.detailsContainer}>
            <Grid container className={classes.containerWrapper} rowSpacing={0}>
              {/* Statut de conduite actuel */}
              <Grid item xs={12} className={classes.header}>
                <Typography variant="h6">Statut de conduite actuel</Typography>
              </Grid>
              <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
                <DateField label="Date de début avec Metua'a" name="startDate" formikMPlus={formikMPlus} />
              </Grid>
              <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
                <TextInput
                  label="Mois d'activité sur la plateforme"
                  name="monthsActive"
                  type="number"
                  formikMPlus={formikMPlus}
                />
              </Grid>
              <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
                <TextInput
                  label="Taux d'acceptation des courses"
                  name="tripAcceptanceRate"
                  type="number"
                  formikMPlus={formikMPlus}
                  adornment="%"
                  helperText="Doit être supérieur ou égal à 90% pour être éligible"
                />
              </Grid>
              <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
                <TextInput
                  label="Taux d'annulation"
                  name="cancellationRate"
                  type="number"
                  formikMPlus={formikMPlus}
                  adornment="%"
                  helperText="Doit être inférieur à 5% pour être éligible"
                />
              </Grid>
              <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
                <TextInput
                  label="Courses hebdomadaires moyennes"
                  name="averageTripsPerWeek"
                  type="number"
                  formikMPlus={formikMPlus}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider className={classes.sectionDivider} />
              </Grid>

              {/* Véhicule demandé */}
              <Grid item xs={12} className={classes.header}>
                <Typography variant="h6">Véhicule demandé</Typography>
              </Grid>
              <Grid item xs={12} className={classes.textFieldWrapper}>
                <FormControl>
                  <RadioGroup
                    row
                    name="vehicleType"
                    value={formikMPlus.values.vehicleType}
                    onChange={formikMPlus.handleChange}
                  >
                    {VEHICLE_TYPE_OPTIONS.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio color="primary" />}
                        label={option.label}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
                <TextInput
                  label="Marque / Modèle préféré"
                  name="preferredVehicleMake"
                  formikMPlus={formikMPlus}
                  optional
                />
              </Grid>
              <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
                <TextInput
                  label="Couleur du véhicule préférée"
                  name="preferredVehicleColor"
                  formikMPlus={formikMPlus}
                  optional
                />
              </Grid>

              <Grid item xs={12}>
                <Divider className={classes.sectionDivider} />
              </Grid>

              {/* Préférence de remboursement */}
              <Grid item xs={12} className={classes.header}>
                <Typography variant="h6">Préférence de remboursement</Typography>
              </Grid>
              <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
                <SelectInput
                  label="Durée de remboursement"
                  name="repaymentPreference"
                  formikMPlus={formikMPlus}
                  options={REPAYMENT_PREFERENCE_OPTIONS}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider className={classes.sectionDivider} />
              </Grid>

              {/* Identification & Éligibilité */}
              <Grid item xs={12} className={classes.header}>
                <Typography variant="h6">Identification &amp; Éligibilité</Typography>
              </Grid>
              <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
                <TextInput label="Numéro de CNI" name="nationalIdNumber" formikMPlus={formikMPlus} />
              </Grid>
              <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
                <TextInput
                  label="Numéro de permis de conduire"
                  name="driversLicenseNumber"
                  formikMPlus={formikMPlus}
                />
              </Grid>
              <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
                <DateField
                  label="Date d'expiration du permis"
                  name="driversLicenseExpirationDate"
                  formikMPlus={formikMPlus}
                />
              </Grid>
              <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
                <TextInput
                  label="N° de référence du certificat de moralité"
                  name="criminalRecordCertificateReferenceNumber"
                  formikMPlus={formikMPlus}
                  optional
                />
              </Grid>

              <Grid item xs={12}>
                <Divider className={classes.sectionDivider} />
              </Grid>

              {/* Informations du garant */}
              <Grid item xs={12} className={classes.header}>
                <Typography variant="h6">Informations du garant</Typography>
              </Grid>
              <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
                <TextInput label="Nom complet du garant" name="guarantorName" formikMPlus={formikMPlus} />
              </Grid>
              <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
                <SelectInput
                  label="Lien avec le candidat"
                  name="guarantorRelationship"
                  formikMPlus={formikMPlus}
                  options={GUARANTOR_RELATIONSHIP_OPTIONS}
                />
              </Grid>
              <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
                <TextInput
                  label="Téléphone du garant"
                  name="guarantorPhoneNumber"
                  formikMPlus={formikMPlus}
                />
              </Grid>
              <Grid item xs={12} md={6} className={classes.textFieldWrapper}>
                <TextInput label="Numéro de CNI du garant" name="guarantorIdNumber" formikMPlus={formikMPlus} />
              </Grid>
              <Grid item xs={12} className={classes.textFieldWrapper}>
                <TextInput label="Adresse du garant" name="guarantorAddress" formikMPlus={formikMPlus} />
              </Grid>
            </Grid>
          </Grid>
        </section>
      </Grid>
    </div>
  );
};

export default MPlusInformation;
